import React, { useState, useEffect, useRef } from 'react';
import peacockVideo from './assets/peacock-animation.mp4';

const LoadingScreen = ({ onComplete, isDarkTheme }) => {
  const [isVisible, setIsVisible] = useState(true);
  const [dots, setDots] = useState('');
  const onCompleteRef = useRef(onComplete);
  onCompleteRef.current = onComplete;

  // Matrix code characters
  const matrixChars = '01アイウエオカキクケコサシスセソタチツテトナニヌネノハヒフヘホマミムメモヤユヨラリルレロワヲン';

  // Generate matrix columns - bidirectional (top to bottom and bottom to top)
  const generateMatrixColumns = () => {
    const topToBottomColumns = [];
    const bottomToTopColumns = [];
    const columnCount = Math.floor(window.innerWidth / 20);
    
    for (let i = 0; i < columnCount; i++) {
      const columnHeight = Math.floor(Math.random() * 40) + 30; // Increased from 25+15 to 40+30 for much longer columns
      
      // Generate characters for column
      const column = [];
      for (let j = 0; j < columnHeight; j++) {
        column.push({
          char: matrixChars[Math.floor(Math.random() * matrixChars.length)],
          opacity: Math.random() * 0.8 + 0.2,
          delay: Math.random() * 0.5 // Reduced delay for more synchronized appearance
        });
      }
      
      // Randomly assign to top-to-bottom or bottom-to-top
      if (Math.random() > 0.5) {
        topToBottomColumns.push({
          chars: column,
          left: i * 20,
          animationDelay: Math.random() * 2 // Reduced to 2 seconds for better overlap
        });
      } else {
        bottomToTopColumns.push({
          chars: column,
          left: i * 20,
          animationDelay: Math.random() * 2 // Reduced to 2 seconds for better overlap
        });
      }
    }
    
    return { topToBottom: topToBottomColumns, bottomToTop: bottomToTopColumns };
  };

  const [matrixColumns] = useState(() => generateMatrixColumns());

  useEffect(() => {
    // Matrix code rain animation duration: 8 seconds
    const timeout = setTimeout(() => {
      setIsVisible(false);
      setTimeout(() => {
        onCompleteRef.current();
      }, 500);
    }, 8000);

    // Animated dots for loading effect
    const dotInterval = setInterval(() => {
      setDots(prev => prev.length >= 3 ? '' : prev + '.');
    }, 500);

    return () => {
      clearTimeout(timeout);
      clearInterval(dotInterval);
    };
  }, []);

  // Early return AFTER all hooks are called
  if (!isVisible) {
    return null;
  }

  return (
    <div 
      className={`fixed inset-0 z-50 flex items-center justify-center transition-opacity duration-500 overflow-hidden ${
        isVisible ? 'opacity-100' : 'opacity-0'
      } ${isDarkTheme ? 'bg-black' : 'bg-gray-900'}`}
    >
      {/* Matrix Code Rain Background - Bidirectional */}
      <div className="absolute inset-0">
        {/* Top to Bottom Rain */}
        {matrixColumns.topToBottom.map((column, columnIndex) => (
          <div
            key={`ttb-${columnIndex}`}
            className="absolute top-0 flex flex-col text-green-400 font-mono text-sm leading-tight matrix-column-down"
            style={{
              left: `${column.left}px`,
              animationDelay: `${column.animationDelay}s`
            }}
          >
            {column.chars.map((item, charIndex) => (
              <span
                key={charIndex}
                className="block"
                style={{
                  opacity: item.opacity,
                  animationDelay: `${item.delay}s`,
                  color: charIndex === 0 ? '#00ff00' : `rgba(0, 255, 0, ${item.opacity})`
                }}
              >
                {item.char}
              </span>
            ))}
          </div>
        ))}
        
        {/* Bottom to Top Rain */}
        {matrixColumns.bottomToTop.map((column, columnIndex) => (
          <div
            key={`btt-${columnIndex}`}
            className="absolute bottom-0 flex flex-col-reverse text-blue-400 font-mono text-sm leading-tight matrix-column-up"
            style={{
              left: `${column.left}px`,
              animationDelay: `${column.animationDelay}s`
            }}
          >
            {column.chars.map((item, charIndex) => (
              <span
                key={charIndex}
                className="block"
                style={{
                  opacity: item.opacity,
                  animationDelay: `${item.delay}s`,
                  color: charIndex === 0 ? '#0088ff' : `rgba(0, 136, 255, ${item.opacity})`
                }}
              >
                {item.char}
              </span>
            ))}
          </div>
        ))}
      </div>

      {/* Main Logo Container with Neon Border */}
      <div className="relative z-10 flex flex-col items-center justify-center">
        {/* Neon Border Container */}
        <div className="relative p-6">
          {/* Animated Neon Border */}
          <div 
            className="absolute inset-0 rounded-lg border-4 border-transparent"
            style={{
              background: 'linear-gradient(45deg, #00ff00, #0088ff, #ff00ff, #ffff00, #00ff00) border-box',
              WebkitMask: 'linear-gradient(#fff 0 0) padding-box, linear-gradient(#fff 0 0)',
              WebkitMaskComposite: 'subtract',
              mask: 'linear-gradient(#fff 0 0) padding-box, linear-gradient(#fff 0 0)',
              maskComposite: 'subtract',
              animation: 'neonRotate 2s linear infinite'
            }}
          />
          
          {/* Inner Glow */}
          <div className="absolute inset-2 rounded-lg bg-gradient-to-r from-green-400/20 to-blue-400/20 blur-lg" />
          
          {/* Peacock Animation Video */}
          <div className="relative w-48 h-32 flex items-center justify-center bg-black/80 rounded-lg backdrop-blur-sm overflow-hidden">
            {/* Fallback content - always visible behind video */}
            <div className="absolute inset-0 flex flex-col items-center justify-center h-full w-full">
              <div className="text-5xl mb-2 animate-pulse">🦚</div>
              <div className="text-xl font-bold text-green-400 tracking-wider">
                ARGUS AI
              </div>
              <div className="text-sm text-green-300">
                CashOut Trading Platform
              </div>
            </div>
            {/* Video overlays fallback when it plays */}
            <video
              autoPlay
              loop
              muted
              playsInline
              preload="auto"
              className="absolute inset-0 w-full h-full object-cover rounded-lg z-10"
              style={{ filter: 'brightness(1.2) contrast(1.1)' }}
              onError={(e) => {
                console.log('Video error, hiding video element');
                e.target.style.display = 'none';
              }}
            >
              <source src={peacockVideo} type="video/mp4" />
            </video>
          </div>
        </div>

        {/* Loading Text */}
        <div className="mt-8 text-center">
          <div className="text-lg font-semibold text-green-400 mb-2">
            Initializing Trading Platform{dots}
          </div>
          <div className="text-sm text-green-300/70">
            Connecting to market data streams
          </div>
        </div>
      </div>

      {/* CSS Animations */}
      <style jsx>{`
        @keyframes matrixFallDown {
          0% { transform: translateY(-150vh); opacity: 0; }
          2% { opacity: 1; }
          98% { opacity: 1; }
          100% { transform: translateY(150vh); opacity: 0; }
        }
        
        @keyframes matrixFallUp {
          0% { transform: translateY(150vh); opacity: 0; }
          2% { opacity: 1; }
          98% { opacity: 1; }
          100% { transform: translateY(-150vh); opacity: 0; }
        }
        
        @keyframes neonRotate {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        
        @keyframes pulse {
          0%, 100% { opacity: 0.4; }
          50% { opacity: 1; }
        }
        
        /* Ensure continuous rain for the full duration with longer columns and better timing */
        .matrix-column-down {
          animation: matrixFallDown 5s linear infinite !important;
        }
        
        .matrix-column-up {
          animation: matrixFallUp 5s linear infinite !important;
        }
      `}</style>
    </div>
  );
};

export default LoadingScreen;